import { motion } from "framer-motion";
import { Loader2, Mail, MessageSquare, Send, User } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

// Import Phone Input components
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const ContactForm = ({ isMinimal = false }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = (value) => {
        setFormData({ ...formData, phone: value });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);

        // ... (Your existing URLSearchParams logic here)
        const formDataToSubmit = new URLSearchParams();
        formDataToSubmit.append(process.env.REACT_APP_FORM_NAME, formData.name);
        formDataToSubmit.append(process.env.REACT_APP_FORM_EMAIL, formData.email);
        formDataToSubmit.append(process.env.REACT_APP_FORM_PHONE, formData.phone || "");
        formDataToSubmit.append(process.env.REACT_APP_FORM_SUBJECT, formData.subject);
        formDataToSubmit.append(process.env.REACT_APP_FORM_MESSAGE, formData.message);

        try {
            // Simulated fetch for demonstration
            await fetch(process.env.REACT_APP_GOOGLE_FORM_URL, {
                method: "POST",
                body: formDataToSubmit,
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                mode: 'no-cors'
            });

            await new Promise(resolve => setTimeout(resolve, 1500));
            toast.success("Message sent successfully!");
            setFormData({ name: "", email: "", subject: "", message: "", phone: "" });
        } catch (error) {
            toast.error("Failed to send message.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={isMinimal ? { opacity: 1 } : { opacity: 0, x: 20 }}
            animate={isMinimal ? { opacity: 1 } : { opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className={isMinimal ? "w-full" : "lg:col-span-7"}
        >
            <form
                onSubmit={handleSubmit}
                className={`
                    transition-all duration-300
                    ${isMinimal
                    ? "bg-transparent shadow-none border-none p-0"
                    : "bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-5 sm:p-8 lg:p-10"
                }
                `}
            >
                {/*
                   GRID ADJUSTMENT:
                   Gap reduced on mobile (gap-4) to save vertical space.
                */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    <InputField
                        name="name"
                        label="Full Name"
                        placeholder="John Doe"
                        icon={<User size={18} />}
                        value={formData.name}
                        onChange={handleChange}
                    />
                    <InputField
                        name="email"
                        label="Email Address"
                        type="email"
                        placeholder="john@example.com"
                        icon={<Mail size={18} />}
                        value={formData.email}
                        onChange={handleChange}
                    />

                    <PhoneField
                        label="Phone Number"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                    />

                    <InputField
                        name="subject"
                        label="Subject"
                        placeholder="How can we help?"
                        icon={<MessageSquare size={18} />}
                        value={formData.subject}
                        onChange={handleChange}
                    />
                </div>

                {/* TEXTAREA: Reduced rows on mobile/minimal mode to prevent overflow */}
                <div className="mb-6 sm:mb-8">
                    <label className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1.5 sm:mb-2">
                        Message
                    </label>
                    <textarea
                        name="message"
                        rows={isMinimal ? "3" : "4"}
                        required
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-blue-500/50 transition-all resize-none text-sm sm:text-base"
                        placeholder="Tell us more about your project..."
                    />
                </div>

                <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full py-3.5 sm:py-4 rounded-xl font-bold text-white shadow-lg flex items-center justify-center gap-2 transition-all ${isSubmitting ? "bg-slate-400" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                    {isSubmitting ? <><Loader2 className="animate-spin" size={20} /> Sending...</> : <><Send size={18} className="sm:w-5 sm:h-5" /> Send Message</>}
                </motion.button>
            </form>
        </motion.div>
    );
};

/* ---------------------- PHONE FIELD COMPONENT ----------------------- */
function PhoneField({ label, value, onChange }) {
    return (
        <div className="w-full">
            <label className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1.5 sm:mb-2">
                {label}
            </label>
            <div className="relative">
                <div className="
                    flex items-center px-3 sm:px-4 rounded-xl
                    bg-slate-50 dark:bg-slate-950
                    border border-slate-200 dark:border-slate-800
                    focus-within:ring-2 focus-within:ring-blue-500/50
                    transition-all duration-200
                    h-[46px] sm:h-[50px]
                ">
                    <PhoneInput
                        international
                        defaultCountry="AE"
                        value={value}
                        onChange={onChange}
                        required
                        className="custom-phone-wrapper w-full flex items-center text-sm sm:text-base"
                    />
                </div>
            </div>

            <style jsx="true" global="true">{`
                .PhoneInputCountrySelect {
                    background-color: transparent !important;
                    color: inherit !important;
                }
                .PhoneInputInput {
                    background: transparent !important;
                    border: none !important;
                    outline: none !important;
                    padding-left: 8px !important;
                    color: #f8fafc !important;
                    font-size: inherit !important;
                }
                .dark .PhoneInputCountrySelect option {
                    background-color: #020617 !important;
                    color: #f8fafc !important;
                }
            `}</style>
        </div>
    );
}

/* ---------------------- INPUT FIELD COMPONENT ----------------------- */
function InputField({ label, type = "text", name, value, onChange, placeholder, icon }) {
    return (
        <div>
            <label className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1.5 sm:mb-2">
                {label}
            </label>
            <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors pointer-events-none">
                    {/* Icon scales down slightly on mobile */}
                    {React.cloneElement(icon, { size: 16, className: "sm:w-[18px] sm:h-[18px]" })}
                </div>
                <input
                    name={name}
                    type={type}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    required
                    className="w-full pl-11 pr-4 py-2.5 sm:py-3 rounded-xl bg-slate-50 dark:bg-slate-950
                    text-slate-900 dark:text-slate-100 border border-slate-200
                    dark:border-slate-800 outline-none focus:ring-2
                    focus:ring-blue-500/50 transition-all placeholder:text-slate-400
                    text-sm sm:text-base h-[46px] sm:h-[50px]"
                />
            </div>
        </div>
    );
}

export default ContactForm;